import React, { useEffect, useState } from "react";
import SEO from "../components/SEO";
import GallerySection from "../components/GallerySection";
import ContactForm from "../components/ContactForm";
import { getCMSCollection, getOptimizedImageUrl, getGalleryByRef } from "../lib/cms";

export default function Oprydning() {
  const [galleries, setGalleries] = useState([]);

  useEffect(() => {
    async function loadData() {
      const g = await getCMSCollection("galleries");
      setGalleries(g);
    }
    loadData();
  }, []);

  const gallery =
    getGalleryByRef(galleries, "gallery-oprydning") ||
    getGalleryByRef(galleries, "gallery-forside") ||
    getGalleryByRef(galleries, "oprydning");
  const galleryImages = gallery?.images || [];

  return (
    <>
      <SEO
        title="Oprydning"
        description="Oprydning efter træfældning – fleksible løsninger fra gør-det-selv til fuld service med flishugning, savning i trailerstykker og bortkørsel."
        ogImage="https://assets.auxodev.dk/assets/e431b544-04c3-4ed0-906f-0354609bd752/615369939-881992974627605-6313467560989915482-n.webp"
      />

      {/* Hero Header */}
      <section className="relative bg-[#12281D] text-white py-16 sm:py-24 border-b border-[#1E4231] overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={getOptimizedImageUrl(
              "https://assets.auxodev.dk/assets/e431b544-04c3-4ed0-906f-0354609bd752/615369939-881992974627605-6313467560989915482-n.webp",
              1600,
              80
            )}
            alt="Oprydning efter træfældning"
            className="w-full h-full object-cover opacity-25"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#12281D] via-[#12281D]/80 to-transparent" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white tracking-tight">
              Oprydning
            </h1>
            <p className="mt-4 text-lg sm:text-xl text-[#C0DACB] leading-relaxed">
              Oprydning efter arbejdet – som det passer dig. Fleksible løsninger fra gør-det-selv til fuld service.
            </p>

            <div className="mt-8 flex flex-wrap gap-4 items-center">
              <a
                href="tel:42169230"
                className="inline-flex items-center gap-2.5 bg-[#D9691E] hover:bg-[#C25B15] text-white px-6 py-3.5 rounded-xl font-bold text-sm sm:text-base shadow-lg transition-all whitespace-nowrap"
              >
                <i className="fa-solid fa-phone" aria-hidden="true" />
                <span className="whitespace-nowrap">Ring 42 16 92 30</span>
              </a>
              <a
                href="#kontakt-formular"
                className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white px-6 py-3.5 rounded-xl font-semibold text-sm sm:text-base border border-white/20 transition-all whitespace-nowrap"
              >
                <span>Få uforpligtende tilbud</span>
                <i className="fa-solid fa-arrow-down text-xs" aria-hidden="true" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content Section using Home.jsx overlapping card design */}
      <section className="py-16 sm:py-24 bg-[#F2EDE2]/70 border-y border-[#E2D8C8] overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 items-center gap-y-8 lg:gap-y-0 relative">
            {/* Image Side: Bredere og højere, placeret bag ved tekstboksen med overlap */}
            <div className="lg:col-span-6 lg:row-start-1 lg:col-start-1 relative z-0">
              <div className="relative rounded-2xl overflow-hidden h-[420px] sm:h-[500px] lg:h-[620px] w-full">
                <img
                  src={getOptimizedImageUrl(
                    "https://assets.auxodev.dk/assets/e431b544-04c3-4ed0-906f-0354609bd752/615369939-881992974627605-6313467560989915482-n.webp",
                    1200,
                    85
                  )}
                  alt="Oprydning og flishugning efter træfældning"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            {/* Content Side: Boks med baggrundsfarve #1e5d31, ingen shadows eller border, overlapper billedet foran */}
            <div className="lg:col-span-7 lg:row-start-1 lg:col-start-6 relative z-10 bg-[#1e5d31] rounded-2xl p-6 sm:p-9 lg:p-12 space-y-6 shadow-none border-0">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white tracking-tight">
                Oprydning efter arbejdet – som det passer dig
              </h2>

              <div className="space-y-4 text-base sm:text-lg text-[#D5E5DB] leading-relaxed font-normal">
                <p>
                  Efter fældning kan træet saves i praktiske trailerstykker, så du nemt selv kan stå for oprydning og bortskaffelse.
                </p>
                <p>
                  Ønsker du hjælp, tilbydes komplet oprydning, hvor grene flishugges og flisen kan blive i haven. Stammer og større grene saves i håndterbare længder eller i brændestykker.
                </p>
                <p>
                  Vil du have det hele fjernet, sørges der naturligvis også for fuld bortskaffelse.
                </p>
                <p className="bg-white/10 p-5 rounded-xl border border-white/15 font-medium text-white">
                  <strong>Fleksible løsninger – fra gør-det-selv til fuld service.</strong> Du bestemmer selv, hvor meget du vil have hjælp til.
                </p>
              </div>

              {/* Practical Options Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                <div className="p-4 rounded-xl bg-white/10 border border-white/15">
                  <div className="text-xl text-[#D9691E] mb-2">
                    <i className="fa-solid fa-trailer" aria-hidden="true" />
                  </div>
                  <h3 className="font-bold text-sm text-white">Trailerstykker</h3>
                  <p className="text-xs text-[#C0DACB] mt-1 leading-relaxed">
                    Træet saves i praktiske stykker, så du selv nemt kan læsse det og køre bort på egen trailer.
                  </p>
                </div>

                <div className="p-4 rounded-xl bg-white/10 border border-white/15">
                  <div className="text-xl text-[#D9691E] mb-2">
                    <i className="fa-solid fa-recycle" aria-hidden="true" />
                  </div>
                  <h3 className="font-bold text-sm text-white">Flishugning i haven</h3>
                  <p className="text-xs text-[#C0DACB] mt-1 leading-relaxed">
                    Grene kan flishugges effektivt, og flisen kan genbruges som bunddække i havens bede.
                  </p>
                </div>

                <div className="p-4 rounded-xl bg-white/10 border border-white/15">
                  <div className="text-xl text-[#D9691E] mb-2">
                    <i className="fa-solid fa-fire" aria-hidden="true" />
                  </div>
                  <h3 className="font-bold text-sm text-white">Savning til brænde</h3>
                  <p className="text-xs text-[#C0DACB] mt-1 leading-relaxed">
                    Stammer og større grene saves i brændestykker - klar til at blive kløvet.
                  </p>
                </div>

                <div className="p-4 rounded-xl bg-white/10 border border-white/15">
                  <div className="text-xl text-[#D9691E] mb-2">
                    <i className="fa-solid fa-truck-moving" aria-hidden="true" />
                  </div>
                  <h3 className="font-bold text-sm text-white">Fuld bortskaffelse</h3>
                  <p className="text-xs text-[#C0DACB] mt-1 leading-relaxed">
                    Alt træ, grene og kvas køres væk, så haven efterlades ren, pæn og ryddet.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CONTACT SECTION WITH FORM */}
      <section className="py-16 sm:py-24 bg-[#14291E] text-white relative overflow-hidden" id="kontakt-formular">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 items-center gap-y-8 lg:gap-y-0 relative">
            <div className="lg:col-span-6 lg:row-start-1 lg:col-start-1 relative z-0 space-y-6 lg:pr-8">
              <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight lg:max-w-[75%]">
                Skal jeg stå for oprydningen?
              </h2>
              <p className="text-base sm:text-lg text-[#B9D4C4] leading-relaxed max-w-lg lg:max-w-[75%]">
                Kontakt mig i dag og få en fast aftale og et uforpligtende tilbud på fældning, beskæring og ønsket grad af oprydning.
              </p>

              {/* Direct Info Cards - unified single box overlapping slightly under form to the right */}
              <div className="bg-[#1C3B2C] p-6 sm:p-7 rounded-2xl border border-[#2D5A42] space-y-4 relative z-0 lg:-mr-8 lg:pr-14 shadow-none">
                <div className="flex items-center gap-4 pb-4 border-b border-white/10 text-white">
                  <div className="w-8 flex items-center justify-center shrink-0 text-xl text-[#D9691E]">
                    <i className="fa-solid fa-phone" aria-hidden="true" />
                  </div>
                  <div>
                    <span className="text-xs text-[#A1C5B0] block uppercase tracking-wider font-semibold">
                      Ring direkte
                    </span>
                    <a href="tel:42169230" className="font-extrabold text-lg hover:text-[#F39C5A] transition-colors whitespace-nowrap">
                      42 16 92 30
                    </a>
                  </div>
                </div>

                <div className="flex items-center gap-4 pb-4 border-b border-white/10 text-white">
                  <div className="w-8 flex items-center justify-center shrink-0 text-xl text-[#D9691E]">
                    <i className="fa-regular fa-envelope text-[#D9691E] text-lg" aria-hidden="true" />
                  </div>
                  <div>
                    <span className="text-xs text-[#A1C5B0] block uppercase tracking-wider font-semibold">
                      Send en email
                    </span>
                    <a href="mailto:info@casperstraeservice.dk" className="font-bold text-base hover:text-[#F39C5A] transition-colors whitespace-nowrap">
                      info@casperstraeservice.dk
                    </a>
                  </div>
                </div>

                <div className="flex items-center gap-4 text-white">
                  <div className="w-8 flex items-center justify-center shrink-0 text-xl text-[#D9691E]">
                    <i className="fa-solid fa-location-dot text-[#D9691E] text-lg" aria-hidden="true" />
                  </div>
                  <div>
                    <span className="text-xs text-[#A1C5B0] block uppercase tracking-wider font-semibold">
                      Adresse
                    </span>
                    <span className="text-sm font-medium text-white block">
                      Caspers Træservice • Folevej 23, 6510 Gram
                    </span>
                    <span className="text-xs text-[#8DB59E]">CVR: 37232521</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-7 lg:row-start-1 lg:col-start-6 relative z-10">
              <ContactForm />
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
